import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Hotel } from '@travel/icons/service';
import { checkSearchConditions } from '@travel/traveler-core/components/DataLayer/utils';
import JapanStandardPopup from '@travel/traveler-core/components/JapanStandardPopup';
import Pagination from '@travel/traveler-core/components/Pagination';
import {
  PROVIDER_CONTRACT_TYPES,
  SORT_OPTIONS_DEFAULT_TRANSLATION,
} from '@travel/traveler-core/constants/provider';
import useDeviceType, { MOBILE_SCREEN } from '@travel/traveler-core/hooks/useDeviceType';
import { numOfGuestParser, toSEOSearchString } from '@travel/traveler-core/utils';
import { cx, isNotEmptyArray } from '@travel/utils';

import Breadcrumb from 'components/Breadcrumb';
import CouponAccordion from 'components/CouponAccordion';
import DataLayer from 'components/DataLayer';
import OtherAreas from 'components/OtherAreas';
import WithSkeletonLoading from 'components/WithSkeletonLoading';

import { pushLocation } from 'store/__router/actions';
import { getAreaRelatedItems, getAreaRelatedItemsTitle } from 'store/areaDetails/selectors';
import { getLimitationsForCurrentMarket } from 'store/markets/selectors';
import { fetchMoreProviders } from 'store/providerList/actions';
import {
  getFilters,
  getGspReqId,
  getIsFetchingMore,
  getItems,
  getPlaceName,
  getRequestOffsets,
  getSearchFacilities,
  getSelectedFeatures,
} from 'store/providerList/selectors';
import { getCouponDetails } from 'store/salesPromotion/selectors';

import { autoFilledCheckInDate, autoFilledCheckOutDate } from 'constants/date';
import { OFFER_TYPE } from 'constants/salesPromotion';
import Footer from 'core/App/components/Footer';
import { Translate } from 'core/translate';
import paths from 'core/universalRouter/paths';
import type { SearchFormQuery } from 'ProviderList-Types';

import { getRequestBody } from '../../resolver';
import { getDataLayerPageName } from '../../utils';
import { useMapContext } from '../Map/mapContext';
import ProviderBox from '../ProviderBox';

import styles from './providerList.module.scss';

type Props = {
  /** Object of current page identifier */
  params: { pageIdentifier: string };
  /** Object of current page query */
  query?: SearchFormQuery;
  /** Callback to be called when provider item is being hovered */
  onMouseOver?: (itemId: string) => void;
  /** Flag to define whether the map is expanded or not */
  isExpandedMap?: boolean;
  /** Callback to be called when remove button of coupon name is clicked */
  onRemoveCoupon?: () => void;
};

export type ListTypes = {
  offset?: number;
  limit?: number;
  page?: number;
};
const starRatingKey = 'starRatings[]';

function ProviderList(props: Props) {
  const { isExpandedMap, params, query } = props;

  const { location, zoomLevel } = useMapContext();
  const dispatch = useDispatch();
  const deviceType = useDeviceType();
  const filters = useSelector(getFilters);
  const isMobile = deviceType === MOBILE_SCREEN;
  const limitations = useSelector(getLimitationsForCurrentMarket);

  const searchConditionsAndOr = useMemo(
    () => checkSearchConditions(filters, query?.maxPrice, query?.minPrice),
    [filters, query?.minPrice, query?.maxPrice],
  );
  const couponId = query?.salesPromotionId;
  const couponDetail = useSelector(getCouponDetails(couponId));
  const {
    providers,
    page,
    offset,
    nextOffset,
    limit,
    total,
    areaInfo,
    areaName,
    breadcrumb,
    moreDataAvailable,
  } = useSelector(getItems);

  const gspReqId = useSelector(getGspReqId);

  const requestOffsets = useSelector(getRequestOffsets);
  const placeName = useSelector(getPlaceName);
  const isFetchingMore = useSelector(getIsFetchingMore);
  const searchFacilities: string = useSelector(getSearchFacilities(query?.features));
  const selectedFeatures = useSelector(getSelectedFeatures);
  const areaRelatedItems = useSelector(getAreaRelatedItems);
  const areaRelatedItemsTitle = useSelector(getAreaRelatedItemsTitle);
  // using contract type of list as faceting is client call
  const contractType = providers?.length > 0 ? providers[0]?.contractType : '';
  const isDirectContract = contractType === PROVIDER_CONTRACT_TYPES.DIRECT;
  const isIndirectProviderPriceSort =
    !isDirectContract &&
    (query?.sortKey === 'price_low_to_high' || query?.sortKey === 'price_high_to_low');

  // RAT Params values from providers
  const providerIds = providers ? providers.filter((item) => item).map((item) => item.id) : null;

  // RAT Params values from providers
  const providerPrices = providers?.length
    ? providers.map((item) => item?.price?.total?.toString()).join(',')
    : '';

  const salesPromotions = useMemo(
    () =>
      providers
        ? providers
            .map((item) => item?.price?.salesPromotions?.map((sp) => sp.id))
            .flat()
            .filter((spId) => spId)
        : null,
    [providers],
  );

  const isDatedSearch =
    query?.startDate || query?.endDate || autoFilledCheckInDate() || autoFilledCheckOutDate();
  const onPage = isDatedSearch ? 'providerListDated' : 'providerListUndated';
  const previousOffset = requestOffsets[page - 1] ?? Math.max(offset - limit, 0);
  // in case of MDP moreDataAvailable key will decide if the location has more than one page
  const isMoreThanOnePage = isDirectContract ? total > limit : moreDataAvailable;
  const selectedFilters = selectedFeatures.map((feature) => feature.managementName).join(',');
  const hasJapanStandardProvider = providers?.some((provider) => provider?.isJapanStandard);
  const shouldShowTotalProperties = !isExpandedMap && !isMobile && isDirectContract;
  // in case of MDP and mobile view display the load more button pagination
  const showLoadMore = isMobile || !isDirectContract;

  const onClickPaginationButton = useCallback(
    (nextPaginationOffset, limit, action) => {
      const pageRules = isExpandedMap
        ? action === 'next'
          ? page + 1
          : page - 1
        : nextPaginationOffset / limit + 1;

      const listProps = {
        params: params,
        query: {
          ...query,
          page: pageRules,
          offset: nextPaginationOffset,
        },
      };
      const { [starRatingKey]: starRatingValues, ...restQuery } = listProps.query;
      const searchParams = new URLSearchParams(toSEOSearchString(restQuery, limitations));

      starRatingValues !== undefined &&
        (Array.isArray(starRatingValues)
          ? starRatingValues?.map((value) => searchParams.append(starRatingKey, value))
          : searchParams.append(starRatingKey, starRatingValues));

      if (showLoadMore) {
        dispatch(fetchMoreProviders(getRequestBody({ ...listProps }, couponDetail)));
      } else {
        dispatch(
          pushLocation({
            pathname: paths.providerList.pathResolver(params.pageIdentifier),
            search: searchParams.toString(),
          }),
        );
      }
    },
    [couponDetail, dispatch, params, query, isExpandedMap, showLoadMore, limitations, page],
  );

  const paginationLabelResolver = (reviewCount: string, totalCount: number) => (
    <Translate
      id="Provider_List.Pagination.Hotel.Range"
      data={{
        index: reviewCount,
        num_properies: totalCount,
      }}
    />
  );

  return (
    <section className={styles.container} data-testid="providerList-wrapper">
      <div className={styles.providerListHeader}>
        {isMobile && <div className={styles.swipeDownSign} />}
        <h1 className={cx(couponId && styles.total)} data-testid="providerList-title-text">
          {couponId && (
            <Translate
              id="Coupon_Details.Search_Eligible"
              count={total}
              data={{
                num_eligible_hotels: total,
              }}
            />
          )}
        </h1>
        {isMobile && (
          <>
            {couponId && couponDetail?.offerType !== OFFER_TYPE.DIRECT && (
              <CouponAccordion
                className={styles.couponAccordion}
                onRemoveCoupon={props.onRemoveCoupon}
                couponId={couponId}
              />
            )}
          </>
        )}
      </div>
      <div className={isMobile ? styles.providerItemsSP : ''}>
        {isNotEmptyArray(providers) ? (
          <>
            {providers
              .filter((item) => item)
              .map((item, i) => (
                <ProviderBox
                  index={i}
                  isExpandedMapStyle={isMobile ? true : !!isExpandedMap}
                  item={item}
                  key={item.id}
                  onMouseOver={props.onMouseOver}
                  onPage={onPage}
                  query={query}
                  ratExtraParams={{
                    'data-rat-cp': JSON.stringify({
                      areaid: areaName,
                      fa: searchConditionsAndOr !== 'x' ? '1' : '0',
                      hits: providers?.length,
                      scondid: searchFacilities,
                      sort: query?.sortKey
                        ? SORT_OPTIONS_DEFAULT_TRANSLATION[query?.sortKey]
                        : SORT_OPTIONS_DEFAULT_TRANSLATION.recommended,
                      total_results: total,
                      gsp_reqid: gspReqId,
                    }),
                    'data-rat-oa': searchConditionsAndOr,
                  }}
                />
              ))}

            {isFetchingMore && (
              <WithSkeletonLoading
                className={styles.loaderWrapper}
                skeletonItemClassName={styles.loader}
                skeletonImageClassName={styles.loaderImage}
                isLoading={true}
                count={3}
                length="long"
                customStyle="textWithImageRow"
              />
            )}
          </>
        ) : (
          <div className={styles.noitems}>
            <Hotel size={60} color={'sonicSilver'} />
            <Translate id={'Provider_List.Search_Results.No_Results'} className={styles.title} />
            <Translate id={'Provider_List.No_Results_Subheading'} className={styles.subtitle} />
          </div>
        )}
      </div>
      {isNotEmptyArray(providers) && !isIndirectProviderPriceSort ? (
        isMoreThanOnePage ? (
          <Pagination
            customStyle={showLoadMore ? 'loadmore' : isExpandedMap ? 'nextAndPrev' : 'pagination'}
            total={total}
            limit={limit}
            offset={offset}
            className={styles.loadmoreBox}
            callback={onClickPaginationButton}
            navigationType="simple"
            // NOTE: We have to use nextOffset data from API
            hasNext={
              isDirectContract ? Boolean(nextOffset) && nextOffset !== total : moreDataAvailable
            }
            nextPageOffset={nextOffset}
            hasPrevious={page > 1}
            previousPageOffset={previousOffset}
            paginationLabelResolver={paginationLabelResolver}
            isDisplayPaginationLabel={isDirectContract}
            isLoading={isFetchingMore}
          />
        ) : shouldShowTotalProperties ? (
          <>
            <div className={styles.totalPropertiesTop} />
            <Translate
              className={styles.totalProperties}
              id="Provider_List.Pagination.Hotel.Totals"
              data={{ num_properties: total }}
              count={total}
            />
          </>
        ) : null
      ) : null}
      {/* Note: Common App <Footer /> will not be rendered. Since the map has been covered up the page,
        there is no chance to see footer and breadcrumb. We need to define it in the content part. (Mobile only)
      */}
      {isMobile && (
        <>
          {isNotEmptyArray(areaRelatedItems.trainStations) && (
            <OtherAreas
              title={
                <Translate
                  id="Provider_List.Train_Stations.Title"
                  data={{
                    city_name: areaRelatedItemsTitle,
                  }}
                />
              }
              type="TRAINSTATIONS"
              otherAreas={areaRelatedItems.trainStations}
              wrapperClassName={styles.otherAreasWrapper}
            />
          )}

          {isNotEmptyArray(areaRelatedItems.pointsOfInterest) && (
            <OtherAreas
              title={
                <Translate
                  id="Provider_List.Points_Of_Interest.Title"
                  data={{
                    city_name: areaRelatedItemsTitle,
                  }}
                />
              }
              type="POINTSOFINTEREST"
              otherAreas={areaRelatedItems.pointsOfInterest}
              wrapperClassName={styles.otherAreasWrapper}
            />
          )}

          {isNotEmptyArray(areaRelatedItems.hotSprings) && (
            <OtherAreas
              title={
                <Translate
                  id="Provider_List.Hot_Springs.Title"
                  data={{
                    subdivision_name: areaRelatedItemsTitle,
                  }}
                />
              }
              type="HOTSPRINGS"
              otherAreas={areaRelatedItems.hotSprings}
              wrapperClassName={styles.otherAreasWrapper}
            />
          )}
          <Breadcrumb className={styles.breadcrumb} placeLayers={breadcrumb} />
          <Footer />
        </>
      )}
      {/* for japan quality SEO */}
      {hasJapanStandardProvider && <JapanStandardPopup isUsedForSEO={true} />}
      <DataLayer
        abTestingPattern="NA"
        abTestingVariants="{'NA': 'NA'}"
        areaId={areaName}
        areaName={areaName}
        checkIn={query?.startDate}
        checkOut={query?.endDate}
        childrenAges={query?.childrenAges}
        hotelRegion={areaInfo?.region}
        isSearchFilterApplied={searchConditionsAndOr !== 'x' ? '1' : '0'}
        mapLatitude={location?.lat}
        mapLongitude={location?.lng}
        mapZoom={zoomLevel}
        noOfRooms={query?.noOfUnits}
        numOfAdults={parseInt(query?.adults || '')}
        numOfChildren={query?.childrenAges?.split(',')?.length}
        numOfGuest={numOfGuestParser(query?.adults, query?.childrenAges)}
        pageName={getDataLayerPageName(query)}
        pageType="search"
        placeName={placeName}
        propertyCity={areaInfo?.city}
        propertyCountry={areaInfo?.country}
        propertyDistrict={areaInfo?.district}
        salesPromotions={salesPromotions?.join(',')}
        searchCondition={searchFacilities}
        searchConditionsAndOr={searchConditionsAndOr}
        searchConditionId={searchFacilities}
        searchFilters={selectedFilters}
        searchHits={providers?.length}
        searchPageNumber={page}
        searchQuery={placeName}
        searchSortType={
          query?.sortKey
            ? SORT_OPTIONS_DEFAULT_TRANSLATION[query?.sortKey]
            : SORT_OPTIONS_DEFAULT_TRANSLATION.recommended
        }
        shopIdList={providerIds?.join(',')}
        siteSection="search"
        itemIds={' '} // no IRPs is being showed in list page, agreed with search team that we pass empty here
        gspReqId={gspReqId}
        totalSearchResults={total}
        itemRatePlanPrice={providerPrices} // Use lowest price as price array
        updateData={`${zoomLevel}${location?.lat}${location?.lng}`} // Triggers another call when the zoom or the position of the map are changed
      />
    </section>
  );
}

export default React.memo(ProviderList);
